import { Component } from "@theme/component";
import { fetchConfig } from "@theme/utilities";
import { morphSection } from "@theme/section-renderer";
import { CartUpdateEvent, CartGroupedSections } from "@theme/events";

/**
 * Per-line cart upgrade button.
 *
 * Swaps the current cart line for a higher-priced variant of the same product
 * (resolved in cart-line-upgrade.liquid and passed via data-* attributes).
 *
 * Flow, mirroring the gift-wrap / shipping-protection lifecycle:
 *   1. Re-fetch /cart.js and resolve the current line fresh (key-first, then by
 *      variant id) — makes the swap safe against discount-driven key changes and
 *      lets us carry the line's quantity + properties over to the new variant.
 *   2. Add the target variant (add-first, so a failed add never loses the item),
 *      forwarding selling_plan (subscription) and properties when present.
 *   3. Remove the original line, requesting the grouped cart sections back.
 *   4. Morph only that final response and dispatch a CartUpdateEvent carrying the
 *      full cart JSON as its resource, so sibling listeners skip a /cart.js round-trip.
 */
class CartItemUpgradeButton extends Component {
  #errorTimeout = 0;

  get #nextVariantId() {
    return Number(this.dataset.nextVariantId);
  }

  get #currentVariantId() {
    return Number(this.dataset.currentVariantId);
  }

  get #sellingPlanId() {
    return this.dataset.nextSellingPlanId ? Number(this.dataset.nextSellingPlanId) : null;
  }

  get #lineKey() {
    return this.dataset.lineKey || "";
  }

  /**
   * @param {Event} event
   */
  onUpgrade = async (event) => {
    event.preventDefault();

    const button = this.refs.button;
    if (!(button instanceof HTMLButtonElement) || !this.#nextVariantId) return;

    button.classList.add("btn--loading");
    button.disabled = true;

    try {
      const cart = await this.#fetchCart();
      const line = this.#resolveCurrentLine(cart);
      if (!line) throw new Error("Cart line not found for upgrade");

      await this.#addVariant(line.quantity, line.properties);
      const parsed = await this.#removeCurrent(line.key);
      await this.#applyCartJsonResponse(parsed);
    } catch (error) {
      this.#showLineError(error);
    } finally {
      button.classList.remove("btn--loading");
      button.disabled = false;
    }
  };

  async #fetchCart() {
    const res = await fetch(FoxTheme.routes.cart);
    return res.json();
  }

  /**
   * Finds the line to replace: by stored key first (stable in the common case),
   * falling back to a top-level line matching the current variant id.
   * @param {object} cart
   */
  #resolveCurrentLine(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;

    const key = this.#lineKey;
    let line = key ? cart.items.find((item) => item.key === key) : null;

    if (!line) {
      const variantId = this.#currentVariantId;
      line = cart.items.find(
        (item) => Number(item.variant_id) === variantId && item.parent_relationship?.parent_key == null
      );
    }

    return line;
  }

  /**
   * @param {number} quantity
   * @param {object} [properties]
   */
  async #addVariant(quantity, properties) {
    const item = { id: this.#nextVariantId, quantity };
    if (this.#sellingPlanId) item.selling_plan = this.#sellingPlanId;
    if (properties && Object.keys(properties).length) item.properties = properties;

    const body = JSON.stringify({ items: [item] });
    const res = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
    const parsed = await res.json();

    if (parsed.status && parsed.message) {
      const error = new Error(parsed.message);
      error.serverMessage = parsed.message;
      throw error;
    }

    return parsed;
  }

  /**
   * @param {string} lineKey
   */
  async #removeCurrent(lineKey) {
    const body = JSON.stringify({ id: lineKey, quantity: 0, ...this.#sectionsPayload() });
    const res = await fetch(FoxTheme.routes.cart_change_url, fetchConfig("json", { body }));
    return res.json();
  }

  /**
   * Grouped cart sections to re-render, collected via the CartGroupedSections
   * event bus so every registered section-owner (cart items + drawer) is covered.
   */
  #sectionsPayload() {
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return {
      sections: [...new Set(sections)].join(","),
      sections_url: window.location.pathname.split("?")[0],
    };
  }

  /**
   * @param {object} parsed - Full cart JSON (with `sections`) from cart/change.js.
   */
  async #applyCartJsonResponse(parsed) {
    if (!parsed || !parsed.sections) return;

    const sections = parsed.sections;
    for (const sectionId of Object.keys(sections)) {
      await morphSection(sectionId, sections[sectionId]);
    }

    let itemCount = typeof parsed.item_count === "number" ? parsed.item_count : 0;
    for (const sectionHtml of Object.values(sections)) {
      const countText = new DOMParser()
        .parseFromString(sectionHtml, "text/html")
        .querySelector('[ref="cartItemCount"]')?.textContent;
      const adjustedItemCount = Number.parseInt(countText ?? "", 10);
      if (Number.isFinite(adjustedItemCount)) {
        itemCount = adjustedItemCount;
        break;
      }
    }

    document.querySelectorAll("cart-items-component").forEach((component) => {
      const sectionId = component.dataset.sectionId;
      if (sectionId && sections[sectionId]) {
        component.dispatchEvent(
          new CartUpdateEvent(parsed, sectionId, {
            itemCount,
            source: "cart-item-upgrade-button",
            sections,
          })
        );
      }
    });
  }

  /**
   * Surfaces the failure in this line's existing error box (role="alert").
   * @param {Error & { serverMessage?: string }} error
   */
  #showLineError(error) {
    const message = (error && error.serverMessage) || this.dataset.errorText || "";
    const row = this.closest("tr");
    const container = row?.querySelector(".cart-items__error");
    const textEl = container?.querySelector(".cart-item__error-text");

    if (!(container instanceof HTMLElement) || !(textEl instanceof HTMLElement)) {
      console.error("Cart item upgrade failed:", error);
      return;
    }

    textEl.textContent = message;
    container.classList.remove("hidden");

    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => container.classList.add("hidden"), 5000);
  }
}

if (!customElements.get("cart-item-upgrade-button")) {
  customElements.define("cart-item-upgrade-button", CartItemUpgradeButton);
}
